import { VerificationStep, VerificationStatus, isPendingLikeStatus } from "../common/constant";

// Resolve a possibly-populated tierSetting (string id, or populated { _id }) to its id.
export const tierIdOf = (h) => h?.tierSetting?._id || h?.tierSetting;

const sortByNewest = (histories) =>
    [...(histories || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

/**
 * Newest history record per tier level, plus the failed→blocked relationship:
 * when a lower tier is `verification_failed` and a higher tier is still
 * `draft_verification`, that higher draft is "blocked" and must be cancelled
 * before the failed lower tier can be redone. Shared by all 3 layouts so this
 * logic cannot drift.
 */
export const resolveBlockedTier = (histories = []) => {
    const sorted = sortByNewest(histories);
    const latestByTierLevel = sorted.reduce((acc, h) => {
        if (h && !acc[h.tierLevel]) acc[h.tierLevel] = h;
        return acc;
    }, {});
    const levelHistories = Object.values(latestByTierLevel);
    const failedTierHistory =
        [...levelHistories].sort((a, b) => a.tierLevel - b.tierLevel).find((h) => h.status === "verification_failed") || null;
    const blockedTierHistory = failedTierHistory
        ? levelHistories.find((h) => h.tierLevel > failedTierHistory.tierLevel && h.status === "draft_verification") || null
        : null;
    return {
        blockedByFailedTier: !!(failedTierHistory && blockedTierHistory),
        failedTierHistory,
        blockedTierHistory,
        latestByTierLevel,
    };
};

/**
 * The single tier the client can act on right now, and why.
 *
 * Tiers must be completed in ascending order. Walking levels low→high:
 *   - `verified`                      → passed, keep walking up
 *   - pending / submitted             → awaiting review but does NOT block the
 *                                       next tier (product allows starting the
 *                                       next tier while the current is pending),
 *                                       so keep walking up
 *   - `verification_failed`/`canceled`→ THIS tier is actionable → { reason: "redo" }
 *   - `draft_verification`            → resume it → { reason: "resume" }
 *   - no record / anything else       → begin it → { reason: "start" }
 * If every leveled tier is verified/pending → { reason: "done", tier: null }.
 *
 * The critical property: a FAILED lower tier is returned the moment it is hit,
 * so a higher `verification_submitted` / `pending_verification` tier can never
 * mask a lower failure (that was the bug — the failed tier1 became unreachable
 * once tier2 was submitted). Higher submitted tiers are left untouched.
 */
export const resolveActionableTier = (tiers = [], histories = []) => {
    const latestByLevel = {};
    for (const h of sortByNewest(histories)) {
        const lvl = h?.tierLevel;
        if (Number.isFinite(lvl) && !(lvl in latestByLevel)) latestByLevel[lvl] = h;
    }

    const leveled = [...(tiers || [])]
        .filter((t) => Number.isFinite(t?.tierLevel))
        .sort((a, b) => a.tierLevel - b.tierLevel);

    for (const tier of leveled) {
        const status = latestByLevel[tier.tierLevel]?.status;
        if (status === VerificationStatus.VERIFIED) continue;
        if (isPendingLikeStatus(status)) continue; // awaiting review, not blocking
        if (status === "verification_failed" || status === "canceled")
            return { tier, tierId: tier._id, reason: "redo" };
        if (status === VerificationStatus.DRAFT)
            return { tier, tierId: tier._id, reason: "resume" };
        return { tier, tierId: tier._id, reason: "start" };
    }
    return { tier: null, tierId: null, reason: "done" };
};

/**
 * Decide which verification step to land on, derived purely from
 * profile + tiers + histories. Returns { step } and, when resuming a draft,
 * { selectedTierId } for the caller to apply via setSelectedTier.
 */
export const resolveInitialStep = ({ profile, tiers = [], histories = [] }) => {
    const { blockedByFailedTier } = resolveBlockedTier(histories);
    // Lowest tier the client can act on, and why (see resolveActionableTier).
    const actionable = resolveActionableTier(tiers, histories);
    // Something submitted/pending awaiting review, with nothing actionable.
    const hasPending = sortByNewest(histories).some((h) => isPendingLikeStatus(h?.status));

    // 1. Lower tier failed with a higher DRAFT above it → blocked (cancel flow).
    if (blockedByFailedTier) return { step: VerificationStep.Tier };
    // 2. The lowest actionable tier drives the landing step. `redo` (a failed
    //    lower tier) is surfaced even when higher tiers are already submitted,
    //    so it must beat the `hasPending` "under review" screen below.
    if (actionable.reason === "redo") return { step: VerificationStep.Tier };
    if (actionable.reason === "resume") return { step: VerificationStep.Document, selectedTierId: actionable.tierId };
    if (actionable.reason === "start" && profile?.flowSetting) return { step: VerificationStep.Tier };
    // 3. Nothing to act on but a record is awaiting review (e.g. every tier is
    //    now submitted/pending after a resubmit) → the under-review screen.
    if (hasPending) return { step: VerificationStep.Status };
    // 4. Fallbacks: resume the flow, pick a country, or start fresh.
    if (profile?.flowSetting) return { step: VerificationStep.Tier };
    if (profile?.status === "not_verified") return { step: VerificationStep.Country };
    return { step: VerificationStep.Flow };
};
